import React from 'react';
import { Shield, Award, CheckCircle, Building, Scale, Users, Globe, Star } from 'lucide-react';

const LogoCloud: React.FC = () => {
  const recognitions = [
    {
      icon: Building,
      title: "Section 8 Company",
      subtitle: "License: 167793",
      description: "Registered under Companies Act",
      color: "bg-blue-500"
    },
    {
      icon: Shield,
      title: "NPO Darpan",
      subtitle: "ID: TN/2025/0623595",
      description: "Government of India Registration",
      color: "bg-green-500"
    },
    {
      icon: Award,
      title: "DIPP Certified",
      subtitle: "DIPP205239",
      description: "Department for Promotion of Industry",
      color: "bg-purple-500"
    },
    {
      icon: Scale,
      title: "Legal Advisory",
      subtitle: "High Court of Madras",
      description: "Chamber No. 298",
      color: "bg-orange-500"
    },
    {
      icon: CheckCircle,
      title: "Tamil Nadu Registered",
      subtitle: "Corporate ID",
      description: "U88900TZ2025NPL034526",
      color: "bg-indigo-500"
    },
    {
      icon: Users,
      title: "Community Trusted",
      subtitle: "Coimbatore Based",
      description: "Local Community Support",
      color: "bg-teal-500"
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animated-section">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Recognized & Trusted Partners
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our organization is officially recognized and registered with various government bodies, 
            ensuring transparency and accountability in all our operations.
          </p>
        </div>

        {/* Recognition Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {recognitions.map((recognition, index) => {
            const IconComponent = recognition.icon;
            return (
              <div
                key={index}
                className="bg-card rounded-xl p-6 shadow-subtle dark:shadow-subtle-dark border border-border hover:shadow-lg transition-all duration-300 animated-section group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`${recognition.color} p-3 rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                      {recognition.title}
                    </h3>
                    <p className="text-sm font-medium text-primary mb-2">
                      {recognition.subtitle}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {recognition.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Government Emblem Section */}
        <div className="bg-card rounded-xl p-8 shadow-subtle dark:shadow-subtle-dark border border-border max-w-4xl mx-auto mb-16 animated-section">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                  <Shield className="w-10 h-10 text-orange-600" />
                </div>
              </div>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Government of India Recognition</h3>
              <p className="text-muted-foreground mb-4">
                Arrow Drop Foundation is officially registered and recognized by the Government of India 
                as a legitimate non-profit organization committed to social welfare and community development.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  Section 8 Company
                </span>
                <span className="px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
                  NPO Darpan Registered
                </span>
                <span className="px-3 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-full text-sm font-medium">
                  DIPP Certified
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-8 max-w-4xl mx-auto animated-section">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Trust & Transparency</h3>
            <p className="text-muted-foreground">
              We believe in complete transparency and accountability in all our operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h4 className="font-semibold mb-2">Verified Organization</h4>
              <p className="text-sm text-muted-foreground">
                All our registrations and certifications are publicly verifiable
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-success" />
              </div>
              <h4 className="font-semibold mb-2">Public Accountability</h4>
              <p className="text-sm text-muted-foreground">
                Regular reporting and transparent use of funds and resources
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-accent" />
              </div>
              <h4 className="font-semibold mb-2">Community Trust</h4>
              <p className="text-sm text-muted-foreground">
                Trusted by local communities and partner organizations
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LogoCloud;